"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@rallly/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@rallly/ui/form";
import { Input } from "@rallly/ui/input";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import React from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { setVerificationEmail } from "@/app/[locale]/(auth)/login/actions";
import { Trans, useTranslation } from "@/i18n/client";
import { authClient } from "@/lib/auth-client";
import { trpc } from "@/trpc/client";
import { validateRedirectUrl } from "@/utils/redirect";

function useLoginWithEmailSchema() {
  const { t } = useTranslation();
  return React.useMemo(() => {
    return z.object({
      identifier: z.email(t("validEmail")),
      password: z.string().optional(),
    });
  }, [t]);
}

export function LoginWithEmailForm() {
  const router = useRouter();
  const loginWithEmailSchema = useLoginWithEmailSchema();
  const searchParams = useSearchParams();
  const [showPasswordField, setShowPasswordField] = React.useState(false);
  const form = useForm({
    defaultValues: {
      identifier: "",
      password: "",
    },
    resolver: zodResolver(loginWithEmailSchema),
  });
  const { handleSubmit, formState } = form;
  const { t } = useTranslation();

  const getLoginMethod = trpc.auth.getLoginMethod.useMutation();
  const isPasswordLogin = showPasswordField && !!form.watch("password");

  return (
    <Form {...form}>
      <form
        className="space-y-4"
        onSubmit={handleSubmit(async ({ identifier, password }) => {
          const validatedRedirectTo = validateRedirectUrl(
            searchParams?.get("redirectTo"),
          );

          if (password) {
            const res = await authClient.signIn.email({
              email: identifier,
              password,
            });

            if (res.error) {
              switch (res.error.code) {
                case "INVALID_EMAIL_OR_PASSWORD":
                  form.setError("password", {
                    message: t("passwordOrEmailIncorrect", {
                      defaultValue: "Invalid email or password",
                    }),
                  });
                  break;
                default:
                  form.setError("password", {
                    message: res.error.message,
                  });
                  break;
              }
              return;
            }

            window.location.href = validatedRedirectTo ?? "/";

            return;
          } else {
            if (!showPasswordField) {
              const res = await getLoginMethod.mutateAsync({
                email: identifier,
              });

              if (res.method === "credential") {
                // show password field
                setShowPasswordField(true);
                return;
              }
            }

            const res = await authClient.emailOtp.sendVerificationOtp({
              email: identifier,
              type: "email-verification",
            });

            if (res.error) {
              switch (res.error.code) {
                case "EMAIL_BLOCKED":
                  form.setError("identifier", {
                    message: t("authErrorsEmailBlocked"),
                  });
                  break;
                default:
                  form.setError("identifier", {
                    message: res.error.message,
                  });
                  break;
              }
              return;
            }

            await setVerificationEmail(identifier);
            // redirect to verify page with redirectTo

            router.push(
              `/login/verify${
                validatedRedirectTo
                  ? `?redirectTo=${encodeURIComponent(validatedRedirectTo)}`
                  : ""
              }`,
            );
          }
        })}
      >
        <FormField
          control={form.control}
          name="identifier"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("email")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  large
                  type="text"
                  disabled={formState.isSubmitting}
                  autoFocus={true}
                  placeholder={t("emailPlaceholder")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {showPasswordField ? (
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>
                    <Trans i18nKey="password" defaults="Password" />
                    <span className="ml-1 text-muted-foreground text-xs">
                      <Trans i18nKey="optionalLabel" defaults="(Optional)" />
                    </span>
                  </FormLabel>
                  <Link
                    href={`/forgot-password?email=${encodeURIComponent(
                      form.watch("identifier"),
                    )}`}
                    className="text-muted-foreground text-sm hover:underline"
                  >
                    <Trans
                      i18nKey="forgotPassword"
                      defaults="Forgot password?"
                    />
                  </Link>
                </div>

                <FormControl>
                  <Input
                    autoFocus={true}
                    large
                    type="password"
                    disabled={form.formState.isSubmitting}
                    placeholder="••••••••"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}
        {form.formState.errors.root?.message ? (
          <FormMessage>{form.formState.errors.root.message}</FormMessage>
        ) : null}
        <div>
          <Button
            size="lg"
            loading={form.formState.isSubmitting}
            type="submit"
            className="w-full"
            variant="primary"
          >
            {showPasswordField ? (
              isPasswordLogin ? (
                <Trans
                  i18nKey="loginWithPassword"
                  defaults="Login with password"
                />
              ) : (
                <Trans i18nKey="loginWithEmail" defaults="Login with email" />
              )
            ) : (
              <Trans
                i18nKey="continueWithEmail"
                defaults="Continue with email"
              />
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
